import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, notification, Form, Input } from 'antd';
import { ThemeModal } from '../../pages/Styles/Theme';
import {
  getUserByIdApi,
  getUserByIdReset,
  updateUserApi,
  updateUserReset,
} from '../../store/actions/userAction';
import { getUserId, getUserData, STORAGE_KEYS } from '../../utils/permissions';
import styled from 'styled-components';

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.settings.primary};
  }
  .ant-form-item {
    margin-bottom: 16px;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  height: 44px;
  font-size: 14px;
  &:focus, &:hover {
    border-color: ${({ theme }) => theme.settings.primary};
  }
`;

const ProfileEditModal = (props) => {
  const {
    open,
    onClose,
    getUserById,
    getUserByIdReset,
    getUserByIdState,
    updateUser,
    updateUserReset,
    updateUserState,
  } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userId = getUserId();

  // Fetch user data when modal opens
  useEffect(() => {
    if (open && userId) {
      getUserById(userId);
    }
  }, [open, userId, getUserById]);

  // Handle user data response
  useEffect(() => {
    if (getUserByIdState.apiState === 'success' && getUserByIdState.data) {
      const userData = getUserByIdState.data;
      form.setFieldsValue({
        name: userData.name || '',
        email: userData.email || '',
        mobile: userData.mobile || null,
      });
      getUserByIdReset();
    } else if (getUserByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getUserByIdState.message || 'Failed to fetch profile data',
      });
      getUserByIdReset();
    }
  }, [getUserByIdState, form, getUserByIdReset]);

  // Handle update response
  useEffect(() => {
    if (updateUserState.apiState === 'success') {
      const updatedUser = updateUserState.data;

      // Update localStorage
      const currentUserData = getUserData();
      const updatedUserData = {
        ...currentUserData,
        name: updatedUser.name,
        mobile: updatedUser.mobile,
      };
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUserData));

      notification.success({
        message: 'Success',
        description: updateUserState.message || 'Profile updated successfully',
      });

      setLoading(false);
      updateUserReset();
      onClose();
      // Force reload to update sidebar name
      window.location.reload();
    } else if (updateUserState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateUserState.message || 'Failed to update profile',
      });
      setLoading(false);
      updateUserReset();
    }
  }, [updateUserState, updateUserReset, onClose]);

  // Handle form submit
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const updateData = {
        name: values.name,
        mobile: values.mobile ? Number(values.mobile) : null,
      };

      updateUser(userId, updateData);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'An error occurred. Please try again.',
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <ThemeModal
      title="Edit Profile"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={loading}
      width={450}
      destroyOnClose
      maskClosable={false}
    >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Please enter your name',
                },
              ]}
            >
              <StyledInput placeholder="Enter your name" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Email"
              name="email"
            >
              <StyledInput
                placeholder="Email address"
                readOnly
                disabled
                style={{
                  backgroundColor: '#f5f5f5',
                  cursor: 'not-allowed',
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Mobile"
              name="mobile"
            >
              <StyledInput
                type="number"
                placeholder="Enter mobile number"
              />
            </Form.Item>
          </Col>
        </Row>
      </StyledForm>
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  getUserByIdState: state.user.getUserByIdState,
  updateUserState: state.user.updateUserState,
});

const mapDispatchToProps = (dispatch) => ({
  getUserById: (id) => dispatch(getUserByIdApi(id)),
  getUserByIdReset: () => dispatch(getUserByIdReset()),
  updateUser: (id, data) => dispatch(updateUserApi(id, data)),
  updateUserReset: () => dispatch(updateUserReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEditModal);
